import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  ShieldCheck,
  Zap,
  Users,
  TrendingUp,
  ArrowRight,
  ArrowUpRight,
  Check,
  Send,
  Lock,
} from "lucide-react";
import { Select } from "antd";
import MotionReveal from "../../components/motion/MotionReveal";
import { TitleComponent } from "../../components/TitleDocument";
import { fetchNew, fetchMenu, submitTuVan } from "../../api/newsService";
import { slugify } from "../../components/Sdata";
import renderNoBreak from "../../components/renderNoBreak";
import data from "../../data/dichVuVanHanh.json";
import "./dichvuvanhanh.scss";

// Chuỗi khớp tên chuyên mục chứa 6 gói trong CMS/DB (dùng includes).
// "dịch vụ vận hành" khớp cả tên cũ "Gói dịch vụ vận hành" lẫn tên mới "Dịch vụ vận hành".
const CATEGORY_NAME = "dịch vụ vận hành";

// Tách tiêu đề "TÊN VN (Tên EN)" -> { title, titleEn }
const splitTitle = (raw) => {
  const m = (raw || "").match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  return m ? { title: m[1].trim(), titleEn: m[2].trim() } : { title: raw || "", titleEn: "" };
};

// description = "tóm tắt\nbullet1\nbullet2\nbullet3" -> { summary, bullets }
const splitDescription = (raw) => {
  const lines = (raw || "").split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  return { summary: lines[0] || "", bullets: lines.slice(1) };
};

// Map tên icon trong JSON -> component lucide (khối 04 đặc trưng)
const ICON_MAP = { ShieldCheck, Zap, Users, TrendingUp };

const GRID_ANCHOR = "kham-pha";
const FORM_ANCHOR = "dang-ky-tu-van";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Ảnh placeholder: nếu file chưa tồn tại thì ẩn <img> để lộ nền gradient phía sau
const handleImgError = (e) => {
  e.currentTarget.style.display = "none";
};

const DichVuVanHanh = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { banner, features, whyChoose, form, packagesPlaceholder } = data;

  // Placeholder (dùng khi API chưa seed / bị chặn CORS ở local) - không có link chi tiết
  const placeholderPackages = useMemo(
    () => packagesPlaceholder.map((p) => ({ ...p, detailPath: null })),
    [packagesPlaceholder]
  );

  // Lấy 6 gói từ DB qua API: tìm chuyên mục theo tên -> lấy news trong chuyên mục đó
  const [apiPackages, setApiPackages] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const menuRes = await fetchMenu();
        const cats = menuRes?.data?.data || [];
        const cat = cats.find(
          (c) => c.name && c.name.toLowerCase().includes(CATEGORY_NAME.toLowerCase())
        );
        if (!cat) return;
        const slug = slugify(cat.name);
        const newsRes = await fetchNew("", cat.id, 1, 20);
        const list = newsRes?.data?.data?.lstNews || [];
        const mapped = list.map((item, idx) => {
          const { title, titleEn } = splitTitle(item.title);
          const { summary, bullets } = splitDescription(item.description);
          return {
            badge: String(idx + 1).padStart(2, "0"),
            title,
            titleEn,
            image: item.image,
            summary,
            bullets,
            detailPath: `/${slug}/${item.id}`,
          };
        });
        if (alive && mapped.length > 0) setApiPackages(mapped);
      } catch (e) {
        // Lỗi API/CORS -> giữ placeholder
        // eslint-disable-next-line no-console
        console.warn("Không tải được gói dịch vụ từ API, dùng placeholder:", e?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const packages = apiPackages && apiPackages.length > 0 ? apiPackages : placeholderPackages;

  // Cuộn tới form khi điều hướng từ card /dich-vu-ha-tang (?scroll=form hoặc #dang-ky-tu-van).
  // Dùng xong thì xóa ngay tham số khỏi URL để back/reload không tự cuộn lại (chỉ chạy 1 lần).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wantScroll = params.get("scroll") === "form" || location.hash === `#${FORM_ANCHOR}`;
    if (!wantScroll) return;
    const t = setTimeout(() => {
      scrollToId(FORM_ANCHOR);
      navigate(location.pathname, { replace: true }); // bỏ ?scroll=form / #hash
    }, 250);
    return () => clearTimeout(t);
  }, [location.search, location.hash, location.pathname, navigate]);

  const serviceOptions = useMemo(
    () =>
      packages.map((p) => {
        const label = `${p.badge}. ${p.title}`;
        return { label, value: label };
      }),
    [packages]
  );

  const [formData, setFormData] = useState({
    hoTen: "",
    doanhNghiep: "",
    soDienThoai: "",
    email: "",
    dichVuQuanTam: [],
    nhuCau: "",
  });

  const setField = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null); // { ok: boolean, msg: string }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const res = await submitTuVan({
        hoTen: formData.hoTen,
        doanhNghiep: formData.doanhNghiep,
        soDienThoai: formData.soDienThoai,
        email: formData.email,
        dichVuQuanTam: formData.dichVuQuanTam,
        nhuCau: formData.nhuCau,
      });
      if (res?.data?.errorCode === "00") {
        setSubmitResult({ ok: true, msg: "Gửi yêu cầu thành công! VNPT NET sẽ liên hệ lại với bạn sớm nhất." });
        setFormData({ hoTen: "", doanhNghiep: "", soDienThoai: "", email: "", dichVuQuanTam: [], nhuCau: "" });
      } else {
        setSubmitResult({ ok: false, msg: res?.data?.errorDescription || "Gửi yêu cầu thất bại, vui lòng thử lại." });
      }
    } catch (err) {
      setSubmitResult({ ok: false, msg: "Không gửi được yêu cầu. Vui lòng kiểm tra kết nối và thử lại." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="dvvh">
      <TitleComponent title="Gói dịch vụ vận hành & hỗ trợ kỹ thuật" />

      {/* ================= BANNER + 04 ĐẶC TRƯNG ================= */}
      <div className="dvvh__banner">
        <div className="dvvh__banner-bg" aria-hidden="true">
          <img src={banner.image} alt="" onError={handleImgError} />
        </div>
        <div className="dvvh__banner-overlay" aria-hidden="true" />

        <div className="dvvh__container">
          <MotionReveal direction="up" amount={0.1}>
            <p className="dvvh__eyebrow">{banner.eyebrow}</p>
            <h1 className="dvvh__banner-title">{renderNoBreak(banner.title)}</h1>
            <p className="dvvh__banner-intro">{renderNoBreak(banner.intro)}</p>

            <div className="dvvh__banner-actions">
              <button
                type="button"
                className="dvvh__btn dvvh__btn--primary"
                onClick={() => scrollToId(GRID_ANCHOR)}
              >
                {banner.primaryButton}
                <ArrowRight size={18} strokeWidth={2.2} />
              </button>
              <button
                type="button"
                className="dvvh__btn dvvh__btn--ghost"
                onClick={() => scrollToId(FORM_ANCHOR)}
              >
                {banner.secondaryButton}
              </button>
            </div>
          </MotionReveal>

          <div className="dvvh__features">
            {features.map((f, idx) => {
              const Icon = ICON_MAP[f.icon] || ShieldCheck;
              return (
                <MotionReveal key={f.title} direction="up" delay={0.08 * idx} amount={0.1}>
                  <div className="dvvh__feature">
                    <span className="dvvh__feature-icon">
                      <Icon size={22} strokeWidth={2} />
                    </span>
                    <div>
                      <h3>{f.title}</h3>
                      <p>{f.desc}</p>
                    </div>
                  </div>
                </MotionReveal>
              );
            })}
          </div>
        </div>
      </div>

      {/* ================= KHÁM PHÁ 06 GÓI (LƯỚI 2x3) ================= */}
      <div id={GRID_ANCHOR} className="dvvh__section dvvh__container">
        <MotionReveal direction="up" amount={0.1}>
          <div className="dvvh__section-head">
            <h2>Khám phá 06 gói dịch vụ</h2>
            <p>Giải pháp vận hành &amp; hỗ trợ kỹ thuật toàn diện cho hạ tầng CNTT của doanh nghiệp.</p>
          </div>
        </MotionReveal>

        <div className="dvvh__grid">
          {packages.map((p, idx) => (
            <MotionReveal key={p.badge} direction="up" delay={0.06 * idx} amount={0.08}>
              <article className="dvvh__card">
                <div className="dvvh__card-media">
                  <img src={p.image} alt={p.title} onError={handleImgError} />
                  <span className="dvvh__badge">{p.badge}</span>
                </div>

                <div className="dvvh__card-body">
                  <h3 className="dvvh__card-title">{p.title}</h3>
                  {p.titleEn ? <p className="dvvh__card-title-en">({p.titleEn})</p> : null}
                  <p className="dvvh__card-summary">{p.summary}</p>

                  <ul className="dvvh__card-bullets">
                    {p.bullets?.map((b, i) => (
                      <li key={i}>
                        <Check size={15} strokeWidth={2.4} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {p.detailPath ? (
                    <Link to={p.detailPath} className="dvvh__card-link">
                      Tìm hiểu thêm
                      <ArrowUpRight size={16} strokeWidth={2.2} />
                    </Link>
                  ) : (
                    <button type="button" className="dvvh__card-link" disabled>
                      Tìm hiểu thêm
                      <ArrowUpRight size={16} strokeWidth={2.2} />
                    </button>
                  )}
                </div>
              </article>
            </MotionReveal>
          ))}
        </div>
      </div>

      {/* ================= VÌ SAO CHỌN VNPT NET ================= */}
      <div className="dvvh__why">
        <div className="dvvh__container">
          <MotionReveal direction="up" amount={0.1}>
            <div className="dvvh__section-head dvvh__section-head--light">
              <h2>{whyChoose.heading}</h2>
            </div>
          </MotionReveal>

          <div className="dvvh__stats">
            {whyChoose.stats.map((s, idx) => (
              <MotionReveal key={s.label} direction="up" delay={0.06 * idx} amount={0.1}>
                <div className="dvvh__stat">
                  <span className="dvvh__stat-value">{s.value}</span>
                  <span className="dvvh__stat-label">{s.label}</span>
                </div>
              </MotionReveal>
            ))}
          </div>
        </div>
      </div>

      {/* ================= FORM ĐĂNG KÝ TƯ VẤN ================= */}
      <div id={FORM_ANCHOR} className="dvvh__contact">
        {form.image ? (
          <div className="dvvh__contact-bg" aria-hidden="true">
            <img src={form.image} alt="" onError={handleImgError} />
          </div>
        ) : null}
        <div className="dvvh__contact-overlay" aria-hidden="true" />

        <div className="dvvh__container dvvh__contact-grid">
          <MotionReveal direction="right" amount={0.1} className="dvvh__contact-intro">
            <p className="dvvh__eyebrow dvvh__eyebrow--accent">{form.eyebrow}</p>
            <h2>{form.heading}</h2>
            <ul className="dvvh__contact-bullets">
              {form.bullets.map((b, i) => (
                <li key={i}>
                  <Check size={18} strokeWidth={2.4} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </MotionReveal>

          <MotionReveal direction="left" amount={0.1} className="dvvh__form-wrap">
            <h3 className="dvvh__form-title">{form.formTitle}</h3>
            <form className="dvvh__form" onSubmit={handleSubmit}>
              <div className="dvvh__form-row">
                <input
                  type="text"
                  placeholder="Họ và tên *"
                  required
                  value={formData.hoTen}
                  onChange={setField("hoTen")}
                />
                <input
                  type="text"
                  placeholder="Doanh nghiệp *"
                  required
                  value={formData.doanhNghiep}
                  onChange={setField("doanhNghiep")}
                />
              </div>

              <div className="dvvh__form-row">
                <input
                  type="tel"
                  placeholder="Số điện thoại *"
                  required
                  maxLength={10}
                  pattern="[0-9]{9,10}"
                  value={formData.soDienThoai}
                  onChange={setField("soDienThoai")}
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={setField("email")}
                />
              </div>

              <Select
                mode="multiple"
                allowClear
                className="dvvh__form-select"
                placeholder="Chọn dịch vụ quan tâm"
                options={serviceOptions}
                value={formData.dichVuQuanTam}
                onChange={(vals) =>
                  setFormData((prev) => ({ ...prev, dichVuQuanTam: vals }))
                }
              />

              <textarea
                rows={4}
                placeholder="Nhu cầu của bạn"
                value={formData.nhuCau}
                onChange={setField("nhuCau")}
              />

              <button
                type="submit"
                className="dvvh__btn dvvh__btn--primary dvvh__form-submit"
                disabled={submitting}
              >
                {submitting ? "Đang gửi..." : form.submitLabel}
                <Send size={16} strokeWidth={2.2} />
              </button>

              {submitResult ? (
                <p
                  className={`dvvh__form-result ${
                    submitResult.ok ? "dvvh__form-result--ok" : "dvvh__form-result--err"
                  }`}
                >
                  {submitResult.msg}
                </p>
              ) : null}

              <p className="dvvh__form-note">
                <Lock size={13} strokeWidth={2.2} />
                {form.privacyNote}
              </p>
            </form>
          </MotionReveal>
        </div>
      </div>
    </section>
  );
};

export default DichVuVanHanh;
